import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useKnowledgeBaseStats,
  useDocuments,
  useDocument,
  useCreateDocument,
  useDeleteDocument,
  useKnowledgeBaseSearch,
  type Document,
  type DocumentType,
} from '@/hooks/useKnowledgeBase'
import {
  FileText,
  Plus,
  Search,
  Database,
  Layers,
  Hash,
  Trash2,
  Eye,
  BookOpen,
  HelpCircle,
  FileQuestion,
  ClipboardList,
  GraduationCap,
  File,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

export const Route = createFileRoute('/knowledge-base')({
  component: KnowledgeBasePage,
})

// Document type icons and colors
const DOC_TYPE_CONFIG: Record<DocumentType, { icon: React.ElementType; color: string; label: string }> = {
  sop: { icon: ClipboardList, color: 'bg-blue-100 text-blue-700', label: 'SOP' },
  faq: { icon: HelpCircle, color: 'bg-green-100 text-green-700', label: 'FAQ' },
  policy: { icon: FileText, color: 'bg-purple-100 text-purple-700', label: 'Policy' },
  manual: { icon: BookOpen, color: 'bg-orange-100 text-orange-700', label: 'Manual' },
  training: { icon: GraduationCap, color: 'bg-pink-100 text-pink-700', label: 'Training' },
  other: { icon: File, color: 'bg-gray-100 text-gray-700', label: 'Other' },
}

function KnowledgeBasePage() {
  // State
  const [page, setPage] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [docTypeFilter, setDocTypeFilter] = React.useState<DocumentType | 'all'>('all')

  // Sheet state
  const [selectedDocId, setSelectedDocId] = React.useState<number | null>(null)
  const [sheetOpen, setSheetOpen] = React.useState(false)

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false)

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Queries
  const { data: stats, isPending: statsLoading } = useKnowledgeBaseStats()
  const { data: docsData, isPending: docsLoading } = useDocuments({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    docType: docTypeFilter !== 'all' ? docTypeFilter : undefined,
  })

  const documents = docsData?.data ?? []
  const pagination = docsData?.pagination

  // Handlers
  const handleView = (doc: Document) => {
    setSelectedDocId(doc.id)
    setSheetOpen(true)
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1 p-4 md:p-6 space-y-6">
        {/* Page Title */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Knowledge Base</h1>
            <p className="text-muted-foreground">
              Manage documents for AI-powered semantic search
            </p>
          </div>
          <CreateDocumentDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard
            title="Documents"
            value={stats?.totalDocuments ?? 0}
            icon={FileText}
            loading={statsLoading}
          />
          <StatsCard
            title="Chunks"
            value={stats?.totalChunks ?? 0}
            icon={Layers}
            loading={statsLoading}
          />
          <StatsCard
            title="Tokens"
            value={stats?.totalTokens ?? 0}
            icon={Hash}
            loading={statsLoading}
          />
          <StatsCard
            title="Types"
            value={stats?.uniqueTypes ?? 0}
            icon={Database}
            loading={statsLoading}
          />
        </div>

        {/* Search and Filters */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={docTypeFilter}
                onValueChange={(v) => setDocTypeFilter(v as DocumentType | 'all')}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="sop">SOP</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                  <SelectItem value="policy">Policy</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>

          <CardContent>
            {docsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <FileQuestion className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No documents found</h3>
                <p className="text-muted-foreground mb-4">
                  {search ? 'Try a different search term' : 'Get started by creating a document'}
                </p>
                {!search && (
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Document
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <DocumentCard key={doc.id} document={doc} onView={handleView} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} documents)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Document Detail Sheet */}
        <DocumentSheet
          documentId={selectedDocId}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
        />
      </main>
    </div>
  )
}

// Stats Card Component
function StatsCard({
  title,
  value,
  icon: Icon,
  loading,
}: {
  title: string
  value: number
  icon: React.ElementType
  loading: boolean
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-icon-beige">
            <Icon className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {loading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              <p className="text-2xl font-bold">{value.toLocaleString()}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Document Card Component
function DocumentCard({
  document,
  onView,
}: {
  document: Document
  onView: (doc: Document) => void
}) {
  const config = DOC_TYPE_CONFIG[document.docType ?? 'other']
  const Icon = config.icon

  return (
    <div
      className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
      onClick={() => onView(document)}
    >
      <div className={`p-2 rounded-lg ${config.color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-medium truncate">{document.title}</h3>
          <Badge variant="outline" className="shrink-0">
            {config.label}
          </Badge>
        </div>
        {document.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
            {document.description}
          </p>
        )}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{document.chunkCount ?? 0} chunks</span>
          <span>{((document.fileSize ?? 0) / 1024).toFixed(1)} KB</span>
          <span>
            {formatDistanceToNow(new Date(document.createdAt), { addSuffix: true })}
          </span>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onView(document) }}>
        <Eye className="h-4 w-4" />
      </Button>
    </div>
  )
}

// Document Detail Sheet
function DocumentSheet({
  documentId,
  open,
  onOpenChange,
}: {
  documentId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: doc, isPending } = useDocument(documentId ?? 0)
  const deleteMutation = useDeleteDocument()

  const handleDelete = async () => {
    if (!documentId || !confirm('Are you sure you want to delete this document?')) return
    await deleteMutation.mutateAsync(documentId)
    onOpenChange(false)
  }

  const config = doc ? DOC_TYPE_CONFIG[doc.docType ?? 'other'] : DOC_TYPE_CONFIG.other
  const Icon = config.icon

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${config.color}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <SheetTitle>{doc?.title ?? 'Loading...'}</SheetTitle>
              <SheetDescription>{doc?.description}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {isPending ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : doc ? (
          <div className="mt-6 space-y-6">
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Type</p>
                <Badge variant="outline">{config.label}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Chunks</p>
                <p className="font-medium">{doc.chunks?.length ?? 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Size</p>
                <p className="font-medium">{((doc.fileSize ?? 0) / 1024).toFixed(1)} KB</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <Badge variant={doc.isActive ? 'default' : 'secondary'}>
                  {doc.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>

            {/* Content Preview */}
            <div>
              <h4 className="font-medium mb-2">Content</h4>
              <div className="p-4 rounded-lg bg-muted/50 max-h-64 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-mono">
                  {doc.content}
                </pre>
              </div>
            </div>

            {/* Chunks */}
            {doc.chunks && doc.chunks.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Chunks ({doc.chunks.length})</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {doc.chunks.map((chunk) => (
                    <div key={chunk.id} className="p-3 rounded-lg border text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">Chunk #{chunk.chunkIndex + 1}</span>
                        <span className="text-muted-foreground text-xs">
                          ~{chunk.tokenCount} tokens
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2">{chunk.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

// Create Document Dialog
function CreateDocumentDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [content, setContent] = React.useState('')
  const [docType, setDocType] = React.useState<DocumentType>('other')

  const createMutation = useCreateDocument()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title || !content) return

    await createMutation.mutateAsync({
      title,
      description: description || undefined,
      content,
      docType,
    })

    // Reset form
    setTitle('')
    setDescription('')
    setContent('')
    setDocType('other')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Document
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Document</DialogTitle>
          <DialogDescription>
            Add a new document to the knowledge base. Content will be automatically chunked and embedded for semantic search.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="Document title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="docType">Type</Label>
              <Select value={docType} onValueChange={(v) => setDocType(v as DocumentType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sop">SOP</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                  <SelectItem value="policy">Policy</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              placeholder="Brief description of the document"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Textarea
              id="content"
              placeholder="Paste or type your document content here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={12}
              required
            />
            <p className="text-xs text-muted-foreground">
              ~{Math.ceil(content.length / 4)} tokens estimated
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || !title || !content}>
              {createMutation.isPending ? 'Creating...' : 'Create Document'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
